// app/javascript/components/PartnerPage2/VideoSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import parse from 'html-react-parser';

const VideoSection = ({ 
  title, 
  description,
  buttonText = 'Watch on YouTube',
  videoUrl,
  videoId,
  backgroundColor = '#F5F5F5',
  customClass = '',
  showRelatedVideos = false,
  autoplay = false,
  controls = true,
  modestBranding = true
}) => {
  // Extract video ID from URL if not provided directly
  const getVideoId = () => {
    if (videoId) return videoId;
    if (!videoUrl) return null;
    
    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /youtube\.com\/embed\/([^&\s]+)/
    ];
    
    for (const pattern of patterns) {
      const match = videoUrl.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const embedVideoId = getVideoId();
  const youtubeLink = videoUrl || `https://www.youtube.com/watch?v=${embedVideoId}`;

  // Build YouTube embed URL with parameters
  const getEmbedUrl = () => {
    if (!embedVideoId) return null;
    
    const params = new URLSearchParams({
      rel: showRelatedVideos ? '1' : '0',           // Show/hide related videos
      autoplay: autoplay ? '1' : '0',               // Autoplay
      controls: controls ? '1' : '0',               // Show/hide controls
      modestbranding: modestBranding ? '1' : '0',   // Minimal YouTube branding
      playsinline: '1'                               // Play inline on iOS
    });
    
    return `https://www.youtube.com/embed/${embedVideoId}?${params.toString()}`;
  };

  const embedUrl = getEmbedUrl();

  const sectionClassName = clsx(
    'py-16 md:py-20 px-4',
    customClass
  );

  const containerClassName = clsx(
    'max-w-7xl mx-auto'
  );

  const gridClassName = clsx(
    'flex flex-col lg:flex-row',
    'items-center gap-8 lg:gap-12'
  );

  const videoContainerClassName = clsx(
    'w-full lg:w-1/2'
  );

  const videoWrapperClassName = clsx(
    'relative rounded-xl overflow-hidden',
    'shadow-lg aspect-video'
  );

  const iframeClassName = clsx(
    'absolute inset-0 w-full h-full'
  );

  const placeholderClassName = clsx(
    'bg-gray-300 rounded-xl aspect-video',
    'flex items-center justify-center'
  );

  const contentClassName = clsx(
    'w-full lg:w-1/2',
    'text-center lg:text-left'
  );

  const titleClassName = clsx(
    'text-3xl md:text-4xl font-bold',
    'text-gray-900 mb-4'
  );

  const descriptionClassName = clsx(
    'text-lg text-gray-700 mb-6',
    'leading-relaxed'
  );

  const buttonClassName = clsx(
    'inline-block',
    'bg-slate-900 hover:bg-slate-800',
    'text-white font-semibold',
    'py-3 px-8 rounded-lg text-base',
    'transition-colors duration-200 shadow-md'
  );

  const iframeAllow = [
    'accelerometer',
    'autoplay',
    'clipboard-write',
    'encrypted-media',
    'gyroscope',
    'picture-in-picture'
  ].join('; ');

  return (
    <section 
      className={sectionClassName}
      style={{ backgroundColor }}
    >
      <div className={containerClassName}>
        <div className={gridClassName}>
          {/* Video Player - Left Side */}
          <div className={videoContainerClassName}>
            {embedUrl ? (
              <div className={videoWrapperClassName}>
                <iframe
                  className={iframeClassName}
                  src={embedUrl}
                  title="YouTube video player"
                  frameBorder="0"
                  allow={iframeAllow}
                  allowFullScreen
                />
              </div>
            ) : (
              <div className={placeholderClassName}>
                <p className="text-gray-600">Video unavailable</p>
              </div>
            )}
          </div>

          {/* Text Content - Right Side */}
          <div className={contentClassName}>
            <h2 className={titleClassName}>
              {parse(title)}
            </h2>
            
            {description && (
              <p className={descriptionClassName}>
                {parse(description)}
              </p>
            )}
            
            {buttonText && youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className={buttonClassName}
              >
                {parse(buttonText)}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

VideoSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  buttonText: PropTypes.string,
  videoUrl: PropTypes.string,
  videoId: PropTypes.string,
  backgroundColor: PropTypes.string,
  customClass: PropTypes.string,
  showRelatedVideos: PropTypes.bool,
  autoplay: PropTypes.bool,
  controls: PropTypes.bool,
  modestBranding: PropTypes.bool
};

export default VideoSection;